import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  NgZone,
  provideAppInitializer,
  provideEnvironmentInitializer,
} from '@angular/core';
import * as Sentry from '@sentry/angular';

import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeSr from '@angular/common/locales/sr';
import { enableProdMode } from '@angular/core';
import { getApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFirestore, AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFireFunctionsModule } from '@angular/fire/compat/functions';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { getPerformance, providePerformance } from '@angular/fire/performance';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { akitaDevtools, DevtoolsOptions } from '@datorama/akita';
import { initializeApp } from 'firebase/app';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import { AuthService } from './auth/services/auth.service';
import { AuthGuard } from './auth/guards/auth.guard';
import { DatePipe } from '@angular/common';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { FIREBASE_OPTIONS } from '@angular/fire/compat';
import { USE_EMULATOR as USE_AUTH_EMULATOR } from '@angular/fire/compat/auth';
import { USE_EMULATOR as USE_FIRESTORE_EMULATOR } from '@angular/fire/compat/firestore';
import { REGION, USE_EMULATOR as USE_FUNCTIONS_EMULATOR } from '@angular/fire/compat/functions';
import { PwaService } from './core/services/pwa.service';
import { ReloadOnChunkErrorHandler } from './core/services/reload-on-chunk-error.handler';
import { VersionService } from './core/services/version.service';
import { LoadingService } from './shared/services/loading.service';
import { OrderStatusPipe } from './shared/pipes/order-status.pipe';
import { UserRolePipe } from './shared/pipes/user-role.pipe';
import { CountryCurrencyConverterPipe } from './shared/pipes/country-currency-converter.pipe';

// Register Serbian locale for proper number/currency formatting
registerLocaleData(localeSr);

if (environment.production) {
  enableProdMode();

  // Disable console logging in production for better performance
  // Keep only error logging for Sentry to capture
  const noop = () => {};
  ['log', 'debug', 'info', 'warn'].forEach((method) => {
    // @ts-ignore – runtime override is intentional
    console[method] = noop;
  });

  // Keep error logging for Sentry - wrap to ensure it still works
  const originalError = console.error;
  console.error = (...args: any[]) => {
    // Sentry will capture these errors
    originalError.apply(console, args);
  };
}

if (environment.env !== 'local') {
  Sentry.init({
    dsn: 'https://b89df8a7b487b4c4db9f051f2b2abde1@o4509309090136064.ingest.de.sentry.io/4509311540658257',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    environment: environment.env,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
      }),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost'],
    // Session Replay
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
  });
}

function fetchVersion(buildDetailsService: VersionService) {
  return () => buildDetailsService.getVersion().then((version) => version);
}

function provideErrorHandler() {
  const baseHandler = environment.env !== 'local' ? Sentry.createErrorHandler() : new ErrorHandler();
  return new ReloadOnChunkErrorHandler(baseHandler);
}

function provideAkitaDevtools(options: Partial<DevtoolsOptions> = {}) {
  return provideEnvironmentInitializer(() => {
    akitaDevtools(inject(NgZone), options);
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      appRoutes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
      withRouterConfig({
        paramsInheritanceStrategy: 'emptyOnly',
      })
    ),
    importProvidersFrom(
      BrowserModule,
      MatSnackBarModule,
      MatIconModule,
      MatFormFieldModule,
      AngularFireModule.initializeApp(environment.firebase),
      AngularFirestoreModule,
      AngularFireAuthModule,
      AngularFireFunctionsModule,
      //NgxsReduxDevtoolsPluginModule.forRoot(),
      //NgxsModule.forRoot([UserOrganizationsState], { developmentMode: !environment.production }),
      //NgxSpinnerModule.forRoot({ type: 'ball-scale-multiple' }),
      ServiceWorkerModule.register(environment.production ? 'main-sw-dev.js' : 'main-sw.js', {
        enabled: environment.production,
        registrationStrategy: 'registerImmediately',
      })
    ),
    // CoreModule providers
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    DatePipe,
    // AuthModule providers
    AuthGuard,
    { provide: FIREBASE_OPTIONS, useValue: environment.firebase },
    { provide: USE_AUTH_EMULATOR, useValue: environment.useEmulators ? ['http://localhost:9099'] : undefined },
    { provide: USE_FIRESTORE_EMULATOR, useValue: environment.useEmulators ? ['localhost', 8082] : undefined },
    { provide: USE_FUNCTIONS_EMULATOR, useValue: environment.useEmulators ? ['localhost', 5001] : undefined },
    { provide: REGION, useValue: 'europe-west1' },
    {
      provide: ErrorHandler,
      useFactory: provideErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    AngularFirestore,
    // SharedModule providers
    LoadingService,
    OrderStatusPipe,
    UserRolePipe,
    CountryCurrencyConverterPipe,
    provideAkitaDevtools(),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    providePerformance(() => getPerformance(getApp())),
    provideStorage(() => getStorage(getApp())),
    provideFirestore(() => getFirestore(getApp())),
    provideAuth(() => getAuth(getApp())),
    provideMessaging(() => getMessaging(getApp())),
    provideFunctions(() => getFunctions(getApp())), // ✅ Added Firebase Functions
    provideAppInitializer(() => {
      const pwaService = inject<PwaService>(PwaService);
      const authService = inject<AuthService>(AuthService);
      return pwaService.initPwaPrompt(authService); // Ensure this returns void, Promise, or Observable
    }),
    provideAppInitializer(() => {
      fetchVersion(inject<VersionService>(VersionService));
    }),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideNativeDateAdapter(),
  ],
};
